import React, { useRef, useState, useMemo } from "react";
import Table from "../../../../components/Table/Table";
import {
  useHomeworkSubmissionsTableData,
  useDownloadAll
} from "../../../../hooks/homework";
import HomeworkStatus from "./HomeworkStatus";
import HomeworkFilters from "./HomeworkFilters";
import { Button, Box, Divider, Link } from "@mui/material";
import { any } from "underscore";
import moment from "moment";
import FileDetailPanel from "./FileDetailPanel";
import { useTranslation } from "react-i18next";
import HomeworkFeedbackEditor from "./HomeworkFeedback";
import Alert from "@mui/material/Alert";

const getColumns = (onEmployeeClick, trainingObjectId, isIlt) => [
  {
    title: "Employee",
    field: "user.fullName",
    options: {
      sorting: true
    },
    render: rowData => (
      <Link
        color="primary"
        component="button"
        variant="button"
        onClick={() => onEmployeeClick(rowData)}
      >
        {rowData.user.fullName}
      </Link>
    )
  },
  {
    title: "Folder",
    field: "homework.title",
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 240
    }
  },
  {
    title: "DateAndTimeSubmitted",
    field: "createdAt",
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 240
    },
    render: rowData => moment(rowData.createdAt).format("MM/DD/YYYY h:mm A z")
  },
  {
    title: "Accept/Reject",
    field: "status",
    cellStyle: {
      minWidth: 220
    },
    render: rowData => (
      <HomeworkStatus
        rowData={rowData}
        trainingObjectId={trainingObjectId}
        isIlt={isIlt}
      />
    )
  }
];

const Homework = ({ training_object_id, courseType }) => {
  const tableRef = useRef(null);
  const [query, setQuery] = useState({});
  const [selection, setSelection] = useState([]);

  const [feedbackEditorSubmission, setFeedbackEditorSubmission] = useState(
    null
  );

  const { t } = useTranslation();
  const homeworkSubmissions = useHomeworkSubmissionsTableData(
    training_object_id,
    query,
    feedbackEditorSubmission
  );

  const downloadAll = useDownloadAll(training_object_id, selection);

  const filter = (e, { setSubmitting }) => {
    setQuery(e);
    setSubmitting(false);
  };

  const downloadSelected = () => {
    downloadAll();
  };

  const onSelectionChange = items => {
    setSelection(Object.entries(items).map(([id, value]) => id));
  };

  const handleProvideFeedback = submissionData => {
    setFeedbackEditorSubmission(submissionData);
  };

  const hasPublishedFeedback = homeworkSubmission => {
    return (
      homeworkSubmission.comments &&
      homeworkSubmission.comments[0] &&
      !!homeworkSubmission.comments[0].publishedAt
    );
  };

  const columns = useMemo(
    () =>
      getColumns(
        handleProvideFeedback,
        training_object_id,
        courseType === "ilt"
      ),
    [homeworkSubmissions]
  );

  return (
    <>
      <HomeworkFilters
        onSubmit={filter}
        trainingObjectId={training_object_id}
      />
      <Divider />
      <Box mt={4} display="flex">
        <Alert severity="info">
          {courseType === "ilt"
            ? t("homeworkSubmission.rejectionNoticeForIlt")
            : t("homeworkSubmission.rejectionNotice")}
        </Alert>
      </Box>
      <Box my={4}>
        <Button
          disabled={!any(selection)}
          color="primary"
          onClick={downloadSelected}
          variant="contained"
        >
          {t("homeworkSubmission.downloadAll")}
        </Button>
      </Box>
      <Table
        itemType="record"
        columns={columns}
        filtering={false}
        data={homeworkSubmissions}
        tableRef={tableRef}
        selection={true}
        onSelectionChange={onSelectionChange}
        detailPanel={[
          {
            render: rowData => {
              return (
                <FileDetailPanel
                  onProvideFeedback={() => handleProvideFeedback(rowData)}
                  filePath={rowData.filePath}
                  fileName={rowData.filename}
                  hasPublishedFeedback={hasPublishedFeedback(rowData)}
                />
              );
            }
          }
        ]}
      />
      {feedbackEditorSubmission && (
        <HomeworkFeedbackEditor
          submissionData={feedbackEditorSubmission}
          onClose={() => {
            setFeedbackEditorSubmission(null);
          }}
        />
      )}
    </>
  );
};

export default Homework;
